import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material'
import {
  TCheckoutOrder,
  TCheckoutOrderDetails,
  TCheckoutOrderEmail,
} from '../../../../../core/types/CheckoutOrder'
import ListItemDefault from '../../../../../components/ListItem/ListItemDefault'
import { dateTimeString } from '../../../../../core/functions/dateTime'
import { checkoutOrderEmailStatusColorMap } from '../../CheckoutOrderConsts'
import { useCallback, useEffect, useRef, useState } from 'react'
import OrderController from '../../../../../core/controllers/OrderController'
import { IErrorResponse } from '../../../../../core/types/ErrorResponse'
import { useToast } from '../../../../../core/context/ToastContext'

export interface OrderDetailEmailsProps {
  checkoutOrder?: TCheckoutOrder
  details?: TCheckoutOrderDetails
  loading: boolean
}

const OrderDetailEmails = ({
  checkoutOrder,
  details,
  loading,
}: OrderDetailEmailsProps) => {
  const { showToast } = useToast()
  const [emails, setEmails] = useState<TCheckoutOrderEmail[]>([])

  const searching = useRef(false)

  const refreshEmails = useCallback(async () => {
    if (!checkoutOrder?.id) {
      setEmails([])
      return
    }

    if (searching.current) {
      return
    }
    searching.current = true
    try {
      const response = await OrderController.getCheckoutOrderEmails({
        id: checkoutOrder?.id || '',
      })
      const responseError = response as IErrorResponse
      if (responseError?.error) {
        showToast(responseError.error, 'error')
        return
      }
      const responseData = response as TCheckoutOrderEmail[]
      setEmails(responseData)
    } finally {
      searching.current = false
    }
  }, [checkoutOrder?.id, searching, setEmails])

  useEffect(() => {
    refreshEmails()
  }, [checkoutOrder])

  if (loading || searching.current) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
      </Stack>
    )
  }

  if (emails.length === 0) {
    return (
      <Box sx={{ px: 2, py: 6 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum e-mail foi disparado ainda para este pedido.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {emails.map((email) => {
        const statusConfig = checkoutOrderEmailStatusColorMap[email.status]
        return (
          <ListItemDefault
            key={email.id}
            title={
              <Box width="100%">
                <Chip
                  label={statusConfig.label}
                  size="small"
                  sx={{
                    backgroundColor: statusConfig.backgroundColor,
                    color: statusConfig.color,
                  }}
                />
                <Typography fontSize="12px" fontWeight={500}>
                  {dateTimeString(email.sentDate)}
                </Typography>
              </Box>
            }
            value={
              <Box width="100%">
                <Typography>{email.subject}</Typography>
                <Typography color="primary" fontSize="12px" fontWeight={500}>
                  {email.email}
                </Typography>
              </Box>
            }
          />
        )
      })}
    </Box>
  )
}

export default OrderDetailEmails
